import React from 'react';
import StepHero from './StepHero';

// glowField is accepted but unused on Step 4 — its operational
// defaults (non-exclusive, 1-year, self-service) are meaningful zero-
// config answers, not medians that need to be forced.
// eslint-disable-next-line no-unused-vars
export default function StepPartnership({ inputs, onChange, glowField }) {
  return (
    <section data-testid="step-partnership">
      <StepHero
        sectionLabel="Partnership terms"
        title={<>How will you <em style={emStyle}>structure</em> the deal?</>}
        lede="Exclusivity and term length adjust the per-year multipliers; enrichment and support add fixed annual line items. Defaults are deliberately conservative — non-exclusive, single-year, self-service."
      />

      <div style={listStyle}>
        <ToggleRow
          label="Exclusivity"
          desc="Grant one buyer exclusive access to the dataset. Pharma will pay +40% for this. Off by default."
          on={!!inputs.exclusivity}
          onChange={(v) => onChange('exclusivity', v)}
        />

        <SegmentedRow
          label="Contract length"
          desc="Multi-year deals price down per year but lock in commitment."
          aside={inputs.multiYear === 5 ? '−15%' : inputs.multiYear === 3 ? '−10%' : '—'}
          value={String(inputs.multiYear)}
          onChange={(v) => onChange('multiYear', Number(v))}
          options={[
            { value: '1', label: '1 year', hint: 'no discount' },
            { value: '3', label: '3 years', hint: '−10%' },
            { value: '5', label: '5 years', hint: '−15%' },
          ]}
        />

        <ToggleRow
          accent
          label="Data enrichment"
          desc={
            <>
              You'll deliver one round of follow-up labs &amp; outcomes annually. Adds{' '}
              <strong style={{ color: 'var(--accent)' }}>+$100,000</strong> to pharma annual.
            </>
          }
          on={!!inputs.dataEnrichment}
          onChange={(v) => onChange('dataEnrichment', v)}
        />

        <SegmentedRow
          label="Support level"
          desc="What you commit to deliver beyond the data itself."
          aside={inputs.supportLevel === 'full-service' ? '+$75K' : inputs.supportLevel === 'assisted' ? '+$35K' : '$0'}
          value={inputs.supportLevel}
          onChange={(v) => onChange('supportLevel', v)}
          options={[
            { value: 'self-service', label: 'Self-service', hint: '$0' },
            { value: 'assisted', label: 'Assisted', hint: '+$35K' },
            { value: 'full-service', label: 'Full-service', hint: '+$75K' },
          ]}
          note={
            inputs.supportLevel === 'assisted'
              ? "Assisted: dedicated analyst contact + monthly office hours + quarterly cohort refresh. Common for first-time pharma deals."
              : inputs.supportLevel === 'full-service'
              ? "Full-service: embedded analyst, weekly syncs, custom data products. For strategic / multi-program partnerships."
              : "Self-service: data delivery only. Buyer handles their own analysis."
          }
        />
      </div>
    </section>
  );
}

function ToggleRow({ label, desc, on, onChange, accent }) {
  return (
    <button type="button" onClick={() => onChange(!on)} style={accent && on ? rowAccentStyle : rowStyle} aria-pressed={on}>
      <div>
        <div style={rowLabelStyle}>{label}</div>
        <div style={rowDescStyle}>{desc}</div>
      </div>
      <span style={on ? toggleOnStyle : toggleOffStyle} aria-hidden="true">
        <span style={on ? toggleKnobOnStyle : toggleKnobOffStyle} />
      </span>
    </button>
  );
}

function SegmentedRow({ label, desc, aside, value, onChange, options, note }) {
  return (
    <div style={rowStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <div>
          <div style={rowLabelStyle}>{label}</div>
          <div style={rowDescStyle}>{desc}</div>
        </div>
        <div style={asideStyle}>{aside}</div>
      </div>
      <div style={segStyle}>
        {options.map((o, i) => {
          const on = String(value) === String(o.value);
          const style = {
            ...segOptBase,
            ...(on ? segOptOnStyle : segOptOffStyle),
            ...(i === 0 ? { borderRadius: '4px 0 0 4px' } : i === options.length - 1 ? { borderRadius: '0 4px 4px 0' } : {}),
            ...(i > 0 ? { borderLeft: 0 } : {}),
          };
          return (
            <button key={o.value} type="button" onClick={() => onChange(o.value)} style={style} aria-pressed={on}>
              {o.label}
              {o.hint && <span style={on ? segHintOnStyle : segHintStyle}>{o.hint}</span>}
            </button>
          );
        })}
      </div>
      {note && <div style={noteStyle}>{note}</div>}
    </div>
  );
}

const emStyle = { fontStyle: 'italic', color: 'var(--accent)' };
const listStyle = { marginTop: '32px', display: 'grid', gap: '14px' };

const rowBase = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '16px 18px',
  background: 'var(--surface)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--line)',
  borderRadius: '4px',
  textAlign: 'left',
  width: '100%',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
  color: 'var(--ink)',
  flexWrap: 'wrap',
};
const rowStyle = { ...rowBase, cursor: 'default' };
const rowAccentStyle = { ...rowBase, background: 'var(--accent-bg)', borderColor: 'var(--accent)' };

const rowLabelStyle = { fontSize: '14px', fontWeight: 500, color: 'var(--ink)' };
const rowDescStyle = { fontSize: '12.5px', color: 'var(--muted)', marginTop: '3px', lineHeight: 1.4 };
const asideStyle = { fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: '24px', color: 'var(--accent)', fontWeight: 500 };

const toggleBase = { width: '38px', height: '22px', borderRadius: '11px', position: 'relative', flexShrink: 0, display: 'inline-block' };
const toggleOffStyle = { ...toggleBase, background: 'var(--line)' };
const toggleOnStyle = { ...toggleBase, background: 'var(--accent)' };
const knobBase = { position: 'absolute', top: '2px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.15s' };
const toggleKnobOffStyle = { ...knobBase, left: '2px' };
const toggleKnobOnStyle = { ...knobBase, left: '18px' };

const segStyle = { display: 'flex', width: '100%', marginTop: '14px' };
const segOptBase = {
  flex: 1,
  height: '40px',
  fontSize: '13px',
  fontFamily: 'var(--font)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
};
const segOptOffStyle = { background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--line)' };
const segOptOnStyle = { background: 'var(--ink)', color: 'var(--bg)', border: '1px solid var(--ink)', fontWeight: 500 };
const segHintStyle = { fontSize: '11px', color: 'var(--muted)' };
const segHintOnStyle = { fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)' };

const noteStyle = {
  marginTop: '12px',
  fontSize: '12px',
  color: 'var(--ink2)',
  background: 'var(--paper)',
  padding: '10px 12px',
  borderRadius: '4px',
  border: '1px solid var(--line)',
  lineHeight: 1.5,
  width: '100%',
};
